// The exact columns the coupon importer understands, in template order.
//
// A misspelled header is reported rather than imported, because a column the
// importer does not recognise would otherwise arrive as a blank field with no
// indication anything was lost. Keep this list in step with the @PropertyName
// mappings on the backend Coupon model.
//
// There is no category column: a coupon inherits the category of its store, so
// categorising is done once on the store rather than repeated — and possibly
// contradicted — on every row.
export const COUPON_CSV_COLUMNS = [
  "offer_id",
  "title",
  "description",
  "label",
  "code",
  "featured",
  "source",
  "deeplink",
  "affiliate_link",
  "cashback_link",
  "url",
  "image_url",
  "brand_logo",
  "type",
  "store",
  "merchant_home_page",
  "start_date",
  "end_date",
  "status",
  "primary_location",
  "locations",
  "language",
  "rating",
];

// Each CSV column mapped to the field name the API expects. A column missing
// from here is ignored, which is why a misspelled header shows up as a blank
// cell in the review table rather than silently reaching the database.
export const CSV_TO_API = {
  offer_id: "offerId",
  title: "title",
  description: "description",
  label: "label",
  code: "couponCode",
  featured: "isFeatured",
  source: "source",
  deeplink: "deeplink",
  affiliate_link: "affiliateLink",
  cashback_link: "cashbackLink",
  url: "url",
  image_url: "imageUrl",
  brand_logo: "brandLogoUrl",
  type: "type",
  store: "store",
  merchant_home_page: "merchantUrl",
  start_date: "startDate",
  end_date: "endDate",
  status: "status",
  primary_location: "primaryLocation",
  locations: "locations",
  language: "language",
  rating: "rating",
};

// Filled-in rows showing the formats the importer expects.
//
// `store` must already exist — rows for an unknown store are refused rather
// than stored where nothing can render them. The store also supplies the
// category, so there is nothing to set here for it.
const DEMO_ROWS = [
  {
    offer_id: "01012026001",
    title: "20% Off Sitewide",
    description: "Apply this code at checkout to take 20% off your entire order.",
    label: "20% OFF",
    code: "SAVE20",
    affiliate_link: "https://www.example.com/?ref=affiliate",
    url: "https://www.example.com/",
    type: "Code",
    store: "example.com",
    merchant_home_page: "https://www.example.com/",
    start_date: "01/01/2026",
    end_date: "31/12/2026",
    status: "",
    primary_location: "US",
    locations: "US",
    language: "en",
    rating: "5",
  },
  {
    offer_id: "01012026002",
    title: "Free Shipping on Orders Over $50",
    description: "No code needed — the discount is applied automatically at checkout.",
    label: "FREE SHIPPING",
    code: "",
    affiliate_link: "https://www.example.com/?ref=affiliate",
    url: "https://www.example.com/",
    type: "Deal",
    store: "example.com",
    merchant_home_page: "https://www.example.com/",
    start_date: "01/01/2026",
    end_date: "31/12/2026",
    status: "new",
    primary_location: "US",
    locations: "US",
    language: "en",
    rating: "4",
  },
];

// Quote a cell whenever the value would otherwise break the row apart.
const escapeCell = (value) => {
  const cell = value == null ? "" : String(value);
  return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
};

const toCsv = (columns, rows) =>
  [
    columns.join(","),
    ...rows.map((row) =>
      columns.map((c) => escapeCell(c === "offer_id" ? excelSafe(row[c]) : row[c])).join(",")
    ),
  ].join("\r\n") + "\r\n";

export const buildDemoCsv = () => toCsv(COUPON_CSV_COLUMNS, DEMO_ROWS);

// Header row on its own, for when the example rows would just be deleted.
export const buildTemplateCsv = () => toCsv(COUPON_CSV_COLUMNS, []);

// A CSV parser that understands quoted cells, escaped quotes and newlines
// inside a field. Splitting on commas breaks on the very first description
// containing one, which is how a column shift reaches the database unnoticed.
export const parseCsv = (text) => {
  const clean = text.replace(/^﻿/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < clean.length; i += 1) {
    const char = clean[i];
    if (quoted) {
      if (char === '"') {
        if (clean[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && clean[i + 1] === "\n") i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
};

const EXCEL_SCIENTIFIC = /^\d(\.\d+)?[eE][+-]?\d+$/;

// Offer ids follow DDMMYYYY plus a sequence -- 300720260095 is the full form.
// Twelve digits is that convention at its longest; anything beyond it is not an
// id from this scheme.
export const MAX_OFFER_ID_DIGITS = 12;

/**
 * Turns Excel's scientific notation back into plain digits.
 *
 * "1.0082E+12" becomes "1008200000000". The zeros are Excel's, not yours: it
 * kept five significant digits and discarded the rest when it reformatted the
 * cell, so the id is numeric again but no longer the id you typed. Callers warn
 * about that rather than treating the result as correct.
 */
export const expandScientific = (value) => {
  const raw = String(value ?? "").trim();
  if (!EXCEL_SCIENTIFIC.test(raw)) return raw;
  const [mantissa, exponent] = raw.toLowerCase().split("e");
  const digits = mantissa.replace("-", "").replace(".", "");
  const pointAt = mantissa.replace("-", "").indexOf(".");
  const shift = (pointAt === -1 ? mantissa.replace("-", "").length : pointAt) + parseInt(exponent, 10);
  return shift >= digits.length ? digits.padEnd(shift, "0") : digits.slice(0, shift);
};

// Excel opens a bare 12-digit number as scientific notation. Wrapping the cell
// as a formula keeps it text on the way in, and the parser unwraps it on the
// way back out, so a file can survive a round trip through a spreadsheet.
const excelSafe = (value) => (/^\d{10,}$/.test(String(value ?? "")) ? `="${value}"` : value);
const unwrapExcel = (value) => String(value ?? "").replace(/^="(.*)"$/, "$1");

/**
 * Everything wrong with one row, worked out from its current values.
 *
 * Deliberately recomputed rather than stored: an error captured when the file
 * was parsed cannot know that the offer id has since been corrected, so a
 * cached list leaves the row complaining about a problem the editor has
 * already fixed.
 *
 * `siblings` is the other rows in the same import, so a duplicate can name the
 * row it collides with. `knownStores` is the set of existing store ids; when it
 * is missing the store check is skipped here and the API still enforces it.
 */
export const validateCoupon = (coupon, { knownStores, siblings } = {}) => {
  const errors = [];
  const offerId = (coupon.offerId || "").trim();
  const store = (coupon.store || "").trim();
  const title = (coupon.title || "").trim();

  if (!offerId) {
    errors.push("Offer ID is required");
  } else if (!/^\d+$/.test(offerId)) {
    errors.push(`Offer ID "${offerId}" must be digits only — no letters, spaces or punctuation`);
  } else if (coupon.__excelMangled && offerId === expandScientific(coupon.__excelMangled)) {
    // Still the untrusted expansion. Comparing rather than relying on the
    // marker being cleared means typing a real id fixes the row on its own.
    // Expanded back to digits, but Excel kept only the leading few and padded
    // the rest with zeros, so this is not the id that was typed.
    errors.push(
      `Excel had already shortened this id to "${coupon.__excelMangled}" — the digits after that were lost. Re-enter the full offer ID, then format the column as Text.`
    );
  } else if (offerId.length > MAX_OFFER_ID_DIGITS) {
    errors.push(
      `Offer ID "${offerId}" is ${offerId.length} digits — the format is DDMMYYYY plus a sequence, at most ${MAX_OFFER_ID_DIGITS} digits (e.g. 300720260095)`
    );
  }

  if (!store) {
    errors.push("Store is required");
  } else if (knownStores && !knownStores.has(store)) {
    // A near match is almost always a capitalisation or www. slip, and saying
    // so is more useful than "does not exist".
    const suggestion = [...knownStores].find(
      (id) => id.toLowerCase().replace(/^www\./, "") === store.toLowerCase().replace(/^www\./, "")
    );
    errors.push(
      suggestion
        ? `Store "${store}" does not exist — did you mean "${suggestion}"?`
        : `Store "${store}" does not exist — create it first`
    );
  }

  if (!title) {
    errors.push("Title is required");
  }

  if (siblings && offerId && store) {
    const clash = siblings.find(
      (other) =>
        other !== coupon &&
        (other.store || "").trim() === store &&
        (other.offerId || "").trim() === offerId
    );
    if (clash) {
      errors.push(`Duplicate of row ${clash.__row} — same store and offer ID`);
    }
  }

  return errors;
};

/**
 * Turns a parsed CSV into coupon objects plus the problems worth showing before
 * anything is saved. Every check here mirrors one the API enforces, so the
 * review table can flag a row instead of the admin discovering it as a failure.
 */
export const couponsFromCsv = (text, knownStores = null) => {
  const rows = parseCsv(text);
  if (!rows.length) {
    return { coupons: [], headerProblems: ["The file is empty."] };
  }

  const header = rows[0].map((h) => h.trim().toLowerCase());
  const headerProblems = [];
  const unknown = header.filter((h) => h && !CSV_TO_API[h]);
  if (unknown.length) {
    headerProblems.push(`Unrecognised column${unknown.length > 1 ? "s" : ""}: ${unknown.join(", ")}`);
  }
  ["offer_id", "store", "title"].forEach((required) => {
    if (!header.includes(required)) headerProblems.push(`Missing required column: ${required}`);
  });

  const coupons = rows
    .slice(1)
    .map((cells, index) => {
      const coupon = { __row: index + 2 };
      header.forEach((column, i) => {
        const field = CSV_TO_API[column];
        if (field) coupon[field] = unwrapExcel((cells[i] ?? "").trim());
      });
      // A cell Excel has already reformatted arrives as "1.0082E+12". It is put
      // back into digits so no id is ever stored in that form; the row is
      // flagged separately because the digits it lost cannot be recovered.
      if (EXCEL_SCIENTIFIC.test(coupon.offerId || "")) {
        coupon.__excelMangled = coupon.offerId;
        coupon.offerId = expandScientific(coupon.offerId);
      }
      return coupon;
    })
    // Excel leaves trailing rows behind that carry a value in one stray column
    // and nothing else. They are not coupons, and surfacing them as rows that
    // can never be saved is noise the editor has to clear by hand.
    .filter((coupon) => coupon.offerId || coupon.store || coupon.title || coupon.couponCode);

  return { coupons, headerProblems };
};

export const downloadCsv = (fileName, contents) => {
  // BOM so Excel opens the file as UTF-8 rather than the system codepage.
  const blob = new Blob(["﻿", contents], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
